import React from "react";
import {
	Box,
	FormControl,
	HStack,
	Input,
	Select,
	VStack,
} from "@chakra-ui/react";

import type { FormErrors, MatrimonyFormData } from "src/types/matrimonyForm.types";
import {
	FieldGrid,
	Label,
	SectionHeader,
	commonInputProps,
	commonSelectProps,
	maybeErrorText,
	sectionCardProps,
} from "../matrimonyForm.ui";
import { FAMILY_STATUS_OPTIONS } from "src/utils/matrimonyOptions";
import { FIELD_LABELS } from "src/utils/matrimonyFieldLabels";

type Props = {
	formData: MatrimonyFormData;
	errors: FormErrors;
	familyType: string;
	setFamilyType: (value: string) => void;
	updateField: (field: keyof MatrimonyFormData, value: string) => void;
	renderSegmentedButton: (
		optionValue: string,
		selectedValue: string,
		setSelectedValue: (value: string) => void,
		label: string
	) => React.ReactNode;
};

const Step3FamilyBackground: React.FC<Props> = ({
	formData,
	errors,
	familyType,
	setFamilyType,
	updateField,
	renderSegmentedButton,
}) => {
	return (
		<VStack align="stretch" spacing={{ base: 4, md: 6 }}>
			<Box {...sectionCardProps}>
				<SectionHeader title="PARENT'S INFORMATION" />
				<Box mt={3}>
					<FieldGrid>
						<FormControl
							flex="1"
							isRequired
							isInvalid={Boolean(errors.fatherName)}
						>
							<Label>{FIELD_LABELS.FATHER_NAME.toUpperCase()}</Label>
							<Input
								{...commonInputProps}
								name="fatherName"
								autoComplete="off"
								value={formData.fatherName}
								onChange={(event) =>
									updateField("fatherName", event.target.value)
								}
								placeholder="Enter the full name"
							/>
							{maybeErrorText(errors.fatherName)}
						</FormControl>
						<FormControl
							flex="1"
							isRequired
							isInvalid={Boolean(errors.motherName)}
						>
							<Label>{FIELD_LABELS.MOTHER_NAME.toUpperCase()}</Label>
							<Input
								{...commonInputProps}
								name="motherName"
								autoComplete="off"
								value={formData.motherName}
								onChange={(event) =>
									updateField("motherName", event.target.value)
								}
								placeholder="Enter the full name"
							/>
							{maybeErrorText(errors.motherName)}
						</FormControl>
					</FieldGrid>

					<FieldGrid>
						<FormControl
							flex="1"
							isRequired
							isInvalid={Boolean(errors.fatherOccupation)}
						>
							<Label>{FIELD_LABELS.FATHER_OCCUPATION.toUpperCase()}</Label>
							<Input
								{...commonInputProps}
								value={formData.fatherOccupation}
								onChange={(event) =>
									updateField("fatherOccupation", event.target.value)
								}
								placeholder="e.g. Businessman"
							/>
							{maybeErrorText(errors.fatherOccupation)}
						</FormControl>
						<FormControl
							flex="1"
							isRequired
							isInvalid={Boolean(errors.motherOccupation)}
						>
							<Label>{FIELD_LABELS.MOTHER_OCCUPATION.toUpperCase()}</Label>
							<Input
								{...commonInputProps}
								value={formData.motherOccupation}
								onChange={(event) =>
									updateField("motherOccupation", event.target.value)
								}
								placeholder="e.g. Businesswoman"
							/>
							{maybeErrorText(errors.motherOccupation)}
						</FormControl>
					</FieldGrid>
				</Box>
			</Box>

			<Box {...sectionCardProps}>
				<SectionHeader title="FAMILY STRUCTURE" />
				<Box mt={3}>
					<FieldGrid>
						<FormControl flex="1" isRequired>
							<Label>{FIELD_LABELS.FAMILY_TYPE.toUpperCase()}</Label>
							<HStack spacing={3}>
								{renderSegmentedButton(
									"Nuclear",
									familyType,
									setFamilyType,
									"Nuclear"
								)}
								{renderSegmentedButton(
									"Joint",
									familyType,
									setFamilyType,
									"Joint"
								)}
							</HStack>
						</FormControl>

						<FormControl
							flex="1"
							isRequired
							isInvalid={Boolean(errors.familyStatus)}
						>
							<Label>{FIELD_LABELS.FAMILY_STATUS.toUpperCase()}</Label>
							<Select
								{...commonSelectProps}
								value={formData.familyStatus}
								onChange={(event) =>
									updateField("familyStatus", event.target.value)
								}
								placeholder="Select status"
							>
								{FAMILY_STATUS_OPTIONS.map((opt) => (
									<option key={opt.value} value={opt.value}>
										{opt.label}
									</option>
								))}
							</Select>
							{maybeErrorText(errors.familyStatus)}
						</FormControl>
					</FieldGrid>

					<FieldGrid>
						<FormControl flex="1">
							<Label>{FIELD_LABELS.NATIVE_PLACE.toUpperCase()}</Label>
							<Input
								{...commonInputProps}
								value={formData.nativePlace}
								onChange={(event) =>
									updateField("nativePlace", event.target.value)
								}
								placeholder="e.g. Varanasi, Uttar Pradesh"
							/>
						</FormControl>

						<FormControl flex="1">
							<Label>{FIELD_LABELS.TOTAL_FAMILY_MEMBERS.toUpperCase()}</Label>
							<Input
								{...commonInputProps}
								type="number"
								value={formData.totalFamilyMembers || ""}
								onChange={(event) => {
									let val = event.target.value;
									// Only allow positive integers (no '-', '+', or non-numeric)
									if (!/^\d*$/.test(val)) {
										return;
									}
									// Prevent leading zeros
									if (val.length > 1 && val.startsWith("0")) {
										val = val.replace(/^0+/, "");
									}
									updateField("totalFamilyMembers", val);
								}}
								onKeyDown={(e) => {
									if (["-", "+", "e", "E"].includes(e.key)) {
									e.preventDefault();
									}
								}}
								placeholder="e.g. 5"
								min={1}
								inputMode="numeric"
								pattern="[0-9]*"
							/>
						</FormControl>
					</FieldGrid>

				</Box>
			</Box>
		</VStack>
	);
};

export default Step3FamilyBackground;
